import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useUserPermissions } from "../../../../UserPermissionsContext";
import { useNavigate } from "react-router";


interface CallLog {
  employee: string;
  agent_name: string;
  call_start_time_: string;
  call_end_time_: string;
  call_duration_hms: string;
  conversation_duration_hms: string;
  caller_status: string;
  destination_status: string;
  call_status: string;
  aws_call_recording_file?: string;
}

interface TicketLog {
  id: number | string;
  heading: string;
  created_at: string;
  updated_at: string;
  created_by_name: string;
  updated_by_name: string;
  remarks?: string | null;
  description?: string;
  teacher_name?: string;
  category_name?: string;
  sub_category_name?: string;
  academic_year_name?: string;
  call_back_time?: string;
  call_logs?: CallLog[];
}


interface Props {
  teacherId: number;
}

const SedTicketLogs: React.FC<Props> = ({ teacherId }) => {
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();

  const [ticketLogs, setTicketLogs] = useState<TicketLog[]>([]);
  const [expandedId, setExpandedId] = useState<number | string | null>(null);

  useEffect(() => {
    fetchTicketLogs();
  }, [teacherId]);

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchTicketLogs = async () => {
    try {
      const res = await fetch(
        `${webApi}/sed/get_sed_teacher_ticket_logs_by_teacher/${teacherId}/`,
        options,
      );
      if (!res.ok) {
        const errorData = await res.json();
        if (errorData.code === "token_not_valid") {
          navigate("/login");
        }
        throw new Error(errorData.detail || "Failed to fetch user data");
      }
      const data = await res.json();

      if (data.results) {
        setTicketLogs(data.results);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleExpand = (id: number | string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatValue = (value?: string | null) => {
    if (!value || value === "None") return "-";
    return value;
  };

  return (
    <div className="container py-4 position-relative">
      {/* Center Line */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          width: "4px",
          height: "100%",
          background: "#ddd",
          transform: "translateX(-50%)",
        }}
      />

      {ticketLogs.map((log, index) => (
        <div key={log.id} className="mb-4 position-relative">
          {/* Heading */}
          <div
            className="rounded-pill text-center py-2 px-3 shadow-sm fw-semibold"
            style={{
              cursor: "pointer",
              background: index % 2 === 0 ? "#bfe4ef" : "#dcdaf5",
              zIndex: 2,
              color: "#000000",
              position: "relative",
            }}
            onClick={() => toggleExpand(log.id)}
          >
            {log.heading}
          </div>

          {/* Expanded Details */}
          {/* {expandedId === log.id && (
            <div className="card shadow-sm mt-3">
              <div className="card-body p-0">
                <table className="table table-bordered mb-0">
                  <tbody>
                    <tr>
                      <th style={{ width: "35%" }}>Teacher Name</th>
                      <td>{formatValue(log.teacher_name)}</td>
                    </tr>
                    <tr>
                      <th>Category</th>
                      <td>{formatValue(log.category_name)}</td>
                    </tr>
                    <tr>
                      <th>Sub Category</th>
                      <td>{formatValue(log.sub_category_name)}</td>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <td>{formatValue(log.description)}</td>
                    </tr>
                    <tr>
                      <th>Callback Time</th>
                      <td>{formatValue(log.call_back_time)}</td>
                    </tr>
                    <tr>
                      <th>Updated By</th>
                      <td>{formatValue(log.updated_by_name)}</td>
                    </tr>
                    <tr>
                      <th>Updated At</th>
                      <td>{new Date(log.updated_at).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <th>Remarks</th>
                      <td>{formatValue(log.remarks)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )} */}
          {expandedId === log.id && (
            <div className="card shadow-sm mt-3">
              <div className="card-body p-0">
                <table className="table table-bordered mb-0">
                  <tbody>
                    {Object.entries({
                      "Teacher Name": log.teacher_name,
                      Category: log.category_name,
                      "Sub Category": log.sub_category_name,
                      Description: log.description,
                      "Callback Time": log.call_back_time,
                      "Updated By": log.updated_by_name,
                      "Updated At": new Date(log.updated_at).toLocaleString(),
                      Remarks: log.remarks,
                    })
                      .filter(
                        ([_, value]) =>
                          value !== null &&
                          value !== undefined &&
                          value !== "" &&
                          value !== "None",
                      )
                      .map(([key, value]) => (
                        <tr key={key}>
                          <th style={{ width: "50%" }}>{key}</th>
                          <td>{String(value)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {/* Call Logs Section */}
                {log.call_logs && log.call_logs.length > 0 && (
                  <div className="p-3">
                    <h5 className="mb-3">Call Logs</h5>

                    {log.call_logs.map((call, index) => (
                      <div key={index} >
                        <div className="card-body">
                          <table className="table table-sm mb-0">
                            <tbody>
                              <tr>
                                <th>Employee</th>
                                <td>{call.employee}</td>
                              </tr>
                              <tr>
                                <th>Agent</th>
                                <td>{call.agent_name}</td>
                              </tr>
                              <tr>
                                <th>Call Start</th>
                                <td>{call.call_start_time_}</td>
                              </tr>
                              <tr>
                                <th>Call End</th>
                                <td>{call.call_end_time_}</td>
                              </tr>
                              <tr>
                                <th>Call Duration</th>
                                <td>{call.call_duration_hms}</td>
                              </tr>
                              <tr>
                                <th>Conversation Duration</th>
                                <td>{call.conversation_duration_hms}</td>
                              </tr>
                              <tr>
                                <th>Status</th>
                                <td>{call.call_status}</td>
                              </tr>

                              {call.aws_call_recording_file && (
                                <tr>
                                  <th>Recording</th>
                                  <td>
                                    <audio controls style={{ width: "100%" }}>
                                      <source
                                        src={call.aws_call_recording_file}
                                        type="audio/mpeg"
                                      />
                                    </audio>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SedTicketLogs;
